import { useEffect, useRef, useState } from "react";
import type { Vehicle } from "./useVehicleStream";
import {
  buildRouteProjection,
  type RawRouteGeometry,
  type RouteProjection,
} from "./routeProjection";
import { fetchStationImage } from "./stationImages";

// An "arrival" is the moment we first see a train within `ARRIVAL_RADIUS_M`
// along its route's canonical polyline of a stop. We remember the last stop
// each vehicle was at and only emit a new message when it changes, so a
// train sitting at a platform for two minutes doesn't spam the thread.

export type ArrivalMessage = {
  id: string;
  vehicleId: string;
  routeShortName?: string;
  routeColor?: string;
  tripHeadsign?: string;
  stopName: string;
  stopId: string;
  ts: number;
  // Either a text body OR a photo (with caption) — never both. Most
  // arrivals are text-only; a smaller fraction are photo-only.
  body?: string;
  imageUrl?: string;
  imageCaption?: string;
};

const ARRIVAL_RADIUS_M = 150;
const MAX_MESSAGES = 200;

// Sentence templates written from the train's perspective. Each is a
// complete, grammatical sentence so we don't accidentally produce things
// like "I is now at …". {stop} is required; {headsign} is optional and
// only used by templates that gate on it below.
const ARRIVAL_TEMPLATES = [
  "Hey! 👋 Just pulled into {stop}. 🚆",
  "Hi 👋 — I just arrived at {stop}. 🛤️",
  "Heads up: 📣 I'm at {stop} now.",
  "Howdy! 🤠 Rolling into {stop}. 🚉",
  "FYI, I just made it to {stop}. ✅",
  "Yo, currently chilling at {stop}. 😎🧊",
  "Ding ding! 🛎️ Now stopping at {stop}.",
  "Knock knock 🚪 — {stop} it is.",
  "Just slid into {stop}. ✨🚆",
  "Update: 📍 I'm parked at {stop}.",
  "Quick stop at {stop}. Doors are open! 🚪✨",
  "Pulled up to {stop}. All aboard? 🎟️🚆",
  "Made it to {stop}! 🎉",
  "Stop #lookatme: 👀 {stop}.",
  "🚆💨 Whoosh — landed at {stop}.",
  "🛑 Brakes on at {stop}.",
  "Guess who's at {stop}? 🙋 This guy.",
  "Coffee break at {stop} ☕ (kidding, no time).",
  "Tap tap 🎤 — is this thing on? Anyway, {stop}.",
  "Currently at {stop}. Don't tell my dispatcher. 🤫",
  "{stop} achieved. 🏆 Side quest complete.",
  "Mood: ✨ at {stop} ✨",
  "Beep boop 🤖 arrival logged: {stop}.",
  "Hot take 🌶️ — {stop} smells like rain today.",
  "Live from {stop}, it's CHOO CHOO I'm a train!!! 🚆",
  "🎶 ♪ rollin' rollin' rollin' ♪ — into {stop}.",
  "{stop}, my old friend. 👋 We meet again.",
  "Status: 📍 {stop}. Vibes: immaculate. ✨",
  "Park it 🅿️ at {stop}. Brief intermission.",
  "Choo choo! 🚂 {stop} is the spot.",
  "Just dropped anchor ⚓ at {stop}.",
  "Welcome to {stop}, please mind the gap. 🪜",
  "Phew. 😅 Made it to {stop} in one piece.",
  "Here we are: {stop}. 🗺️ Right on (ish) time.",
  "Boop. 🔔 {stop}.",
  "Bing! 🔔 New location unlocked: {stop}.",
  "Trundling into {stop} like it's no big deal. 🚆",
  "Squeak. 🛤️ That was me, braking at {stop}.",
  "Fresh arrival 🆕 — {stop}, party of one.",
  "Cruise complete (for now) at {stop}. 🛳️➡️🚆",
  "Pinging in from {stop}. 📡",
  "Hop hop 🐰 — landed at {stop}.",
  "Got a sec? 📱 I'm at {stop}.",
  "{stop} unlocked. 🔓 Achievement get!",
  "Brief cameo at {stop}. 🎬",
  "Standby at {stop}. 🛑 (Don't leave without me, jk.)",
  "Hi hi hi 👋👋👋 from {stop}.",
  "Currently busy looking cool at {stop}. 😎",
  "Plot twist: 🌀 I'm at {stop}.",
  "{stop} appreciation post 📸 (mentally).",
  "Tea spill ☕ — {stop} is actually pretty nice.",
  "Look ma, no hands 🙌 — at {stop}.",
  "Brief encounter with {stop}. ✨",
  "Manifesting good vibes from {stop}. 🔮",
  "Boop beep 🚆 station: {stop}.",
  "Arrived. {stop}. Iconic. 💅",
  "{stop} on the menu today. 🍽️",
  "Sliding into {stop}'s DMs. 💌",
  "Just vibing at {stop}. 🪩",
  "POV: 🎥 you're at {stop} with me.",
  "Bookmark this moment 🔖 — {stop}.",
  "Local stop spotlight: 🔦 {stop}.",
  "Touchdown 🏈 at {stop}.",
  "{stop}, take the wheel. 🛞 (Please don't actually.)",
  "Pulled in for a sec at {stop}. ⏸️",
  "Soft launch 🤫 of my arrival at {stop}.",
  "Hard launch 📣 — I am AT {stop}.",
  "Vroom 🚆 → 🛑 → {stop}.",
  "Ahoy! ⚓ Docking at {stop}.",
  "Ribbit 🐸 (idk why) — {stop}.",
  "Catch me at {stop} ⏱️ before I roll.",
  "Quick check-in: 📲 {stop}.",
];

// Friendly "look at this place" lines used when we attach a station photo.
const IMAGE_CAPTIONS = [
  "You should see this!",
  "Good vibes here! ✨",
  "Look at this view 👀",
  "Snapped this for you 📸",
  "Pretty cool, right?",
  "Wish you were here!",
  "The neighborhood today 🌇",
  "Postcard from the platform 💌",
  "Couldn't resist a quick pic 📷",
  "Today's window seat 🪟",
  "Sending this with love 💕",
  "Thought of you when I saw this 🥹",
  "Local color 🎨",
  "Caught the light just right ☀️",
  "Saving this one for the album 📚",
  "No filter, promise 🤞",
  "Platform aesthetics on point ✨",
  "Just a little hello from the route 👋",
  "Look what I found 🔎",
  "Scene of the day 🎬",
  "This place though 😌",
  "Quick drive-by snapshot 🚆📸",
  "Adding to the gallery 🖼️",
  "Hello from out here 🌤️",
  "Felt cute, took a pic 💁",
  "Brief but make it scenic 🌳",
  "Catch of the day 🎣📷",
  "Sky's doing a thing ⛅",
  "Couldn't not share this 🤷",
  "Sneak peek 👀",
  "Tiny moment of joy ☺️",
  "On location 🎥",
  "Have a look at this corner of the world 🌎",
  "Random beauty alert 🚨✨",
  "From me to you 💌",
  "This one's for you 🎁",
  "Postcard energy 📮",
  "Found a vibe 💫",
  "Architecture appreciation hour 🏛️",
  "Treat for your eyes 👁️✨",
  "Souvenir from the platform 🎟️",
  "Pic dump incoming 📥",
  "Travel diary entry 📖",
  "Mood board material 🧷",
  "Thought you'd like this one 💭",
];

const ARRIVAL_TEMPLATES_WITH_HEADSIGN = [
  "Hey! 👋 Just pulled into {stop}. Next stop on the way to {headsign}. 🚆",
  "Hi 👋 — I'm at {stop}, still bound for {headsign}. 🧭",
  "Howdy! 🤠 Rolling into {stop}. {headsign}, here we come! 🚉",
  "Doors open at {stop}. 🚪 Onward to {headsign}! ➡️",
  "Quick pit stop at {stop} 🛑, then back on the road to {headsign}. 🛤️",
  "Just slid into {stop}. ✨ Final destination: {headsign}. 🏁",
  "FYI — 📣 at {stop} now. {headsign} bound. 🎯",
  "Ding ding! 🛎️ {stop}. Carrying on to {headsign}. 🚆",
  "📍 {stop} ✓ — next up: {headsign}. 🛤️",
  "Brief layover at {stop}. {headsign} or bust! 💪",
  "{stop} ✅ — {headsign}, see you soon. 🫡",
  "Halfway thought from {stop}: {headsign} can't come soon enough. 😮‍💨",
  "Pausing at {stop}. The road to {headsign} continues. 🛣️",
  "{stop} for a sec, then full steam to {headsign}. 💨",
  "Hi from {stop}! Still chasing {headsign}. 🏃",
  "Update: at {stop}, ETA {headsign} is ~soon™. ⏳",
  "Pit stop: {stop}. Destination: {headsign}. Mood: 🚆💨",
  "Bing 🔔 — {stop} now. {headsign} on deck. 🎯",
  "Tiny pause at {stop}, then onward to {headsign}. ⏭️",
  "Hi from {stop}! Big {headsign} energy ahead. ✨",
  "{stop} checkmark ✅ — {headsign} loading… ⏳",
  "Dropping off folks at {stop}. {headsign} crew, stay tuned. 🎟️",
  "Beep 🛎️ {stop}. Soon-ish: {headsign}.",
  "Snack break 🥨 at {stop} (mental only). Onward to {headsign}. 🛤️",
  "Currently: {stop}. Future me: {headsign}. 🔮",
  "{stop} cameo 🎬, headlining {headsign} next.",
  "Detour-free path to {headsign} 🛤️ — quick stop at {stop}.",
  "Moment of zen at {stop}. 🧘 Then off to {headsign}.",
  "Briefly at {stop}. Spoiler: {headsign} is the destination. 🎬",
  "Tap in 📍 {stop}, tap out → {headsign}.",
  "{stop} ⏸️ → {headsign} ▶️",
  "Loading {headsign}… 🔄 Currently buffered at {stop}.",
  "Hot stop alert 🚨 {stop}. {headsign} still on the itinerary.",
  "Brief hello from {stop}. 👋 Bigger hello incoming at {headsign}. 🤗",
];

// FNV-1a 32-bit hash. We avalanche the seed through this with different
// salts so each decision (template pool, template index, image gate, image
// caption) gets an independent-looking stream. Without this, the seed —
// which is dominated by the second-resolution timestamp — only changes by
// small increments between arrivals, so naive `seed % N` and `seed >> k`
// indices land on the same bucket for long stretches.
export function hash32(seed: number, salt: number): number {
  let h = 0x811c9dc5 ^ salt;
  let s = seed >>> 0;
  for (let i = 0; i < 4; i++) {
    h ^= s & 0xff;
    h = Math.imul(h, 0x01000193);
    s >>>= 8;
  }
  // Final mix.
  h ^= h >>> 16;
  h = Math.imul(h, 0x85ebca6b);
  h ^= h >>> 13;
  h = Math.imul(h, 0xc2b2ae35);
  h ^= h >>> 16;
  return h >>> 0;
}

export function pick<T>(arr: T[], seed: number, salt: number): T {
  return arr[hash32(seed, salt) % arr.length];
}

// Salt namespace — pick distinct constants so streams don't correlate.
const SALT_HEADSIGN_GATE = 0xa1;
const SALT_TEMPLATE = 0xb2;
const SALT_IMAGE_GATE = 0xc3;
const SALT_CAPTION = 0xd4;

export function composeBody(stopName: string, headsign: string | undefined, seed: number): string {
  // Mix in the headsign-aware pool about half the time when we have one,
  // so consecutive arrivals on the same trip don't all look the same.
  const useHeadsign = headsign && (hash32(seed, SALT_HEADSIGN_GATE) & 1) === 0;
  const pool = useHeadsign
    ? ARRIVAL_TEMPLATES_WITH_HEADSIGN
    : ARRIVAL_TEMPLATES;
  const tpl = pick(pool, seed, SALT_TEMPLATE);
  return tpl
    .replace("{stop}", stopName)
    .replace("{headsign}", headsign ?? "");
}

type Props = {
  vehicles: Vehicle[];
};

export function useArrivals({ vehicles }: Props) {
  const [projections, setProjections] = useState<RouteProjection[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [messages, setMessages] = useState<ArrivalMessage[]>([]);

  // Per-vehicle last-known stop, so we only emit when the train moves on
  // to a different stop (or appears for the first time near one).
  const lastStopRef = useRef<Map<string, string>>(new Map());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/routes/geometry");
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = (await res.json()) as RawRouteGeometry[];
        if (cancelled) return;
        const projs = data
          .map(buildRouteProjection)
          .filter((p) => p.lengthM > 0);
        setProjections(projs);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!projections || projections.length === 0) return;
    const projByRoute = new Map(projections.map((p) => [p.routeId, p]));

    const newMessages: ArrivalMessage[] = [];
    const seenVehicles = new Set<string>();

    for (const v of vehicles) {
      if (!v.vehicle_id || !v.route_id) continue;
      seenVehicles.add(v.vehicle_id);
      const proj = projByRoute.get(v.route_id);
      if (!proj || proj.stops.length === 0) continue;

      const distM = proj.project(v.lat, v.lon);
      if (distM == null) continue;

      // Find nearest stop along the route.
      let nearest = proj.stops[0];
      let nearestGap = Math.abs(distM - nearest.distM);
      for (let i = 1; i < proj.stops.length; i++) {
        const s = proj.stops[i];
        const gap = Math.abs(distM - s.distM);
        if (gap < nearestGap) {
          nearest = s;
          nearestGap = gap;
        }
      }
      if (nearestGap > ARRIVAL_RADIUS_M) {
        // In transit between stops — clear the "currently at" marker so the
        // next stop arrival registers cleanly.
        lastStopRef.current.delete(v.vehicle_id);
        continue;
      }

      const prev = lastStopRef.current.get(v.vehicle_id);
      if (prev === nearest.id) continue;
      lastStopRef.current.set(v.vehicle_id, nearest.id);

      const ts = v.ts || Math.floor(Date.now() / 1000);
      // Mix every char of the vehicle id into the seed so two trains
      // arriving in the same second don't collide on the same templates.
      let seed = ts >>> 0;
      for (let i = 0; i < v.vehicle_id.length; i++) {
        seed = (Math.imul(seed, 31) + v.vehicle_id.charCodeAt(i)) >>> 0;
      }
      seed = (seed + nearest.id.length * 2654435761) >>> 0;
      const id = `${v.vehicle_id}-${nearest.id}-${ts}`;
      // Most arrivals are text-only; ~1 in 6 is photo-only (image + caption,
      // no body text). We never send both at once.
      const includeImage = hash32(seed, SALT_IMAGE_GATE) % 6 === 0;
      newMessages.push({
        id,
        vehicleId: v.vehicle_id,
        routeShortName: v.route_short_name,
        routeColor: v.route_color,
        tripHeadsign: v.trip_headsign,
        stopName: nearest.name,
        stopId: nearest.id,
        ts,
        body: includeImage
          ? undefined
          : composeBody(nearest.name, v.trip_headsign, seed),
        imageCaption: includeImage
          ? pick(IMAGE_CAPTIONS, seed, SALT_CAPTION)
          : undefined,
      });
    }

    // Forget vehicles that disappeared from the snapshot so they can re-greet
    // when they come back online.
    for (const id of Array.from(lastStopRef.current.keys())) {
      if (!seenVehicles.has(id)) lastStopRef.current.delete(id);
    }

    if (newMessages.length === 0) return;

    // Kick off async photo lookups for any messages flagged for an image.
    // We render the message immediately and patch in the URL once Wikipedia
    // responds; if it 404s we just leave the message text-only.
    for (const m of newMessages) {
      if (!m.imageCaption) continue;
      fetchStationImage(m.stopName).then((url) => {
        if (!url) return;
        setMessages((prev) => {
          const idx = prev.findIndex((x) => x.id === m.id);
          if (idx < 0 || prev[idx].imageUrl) return prev;
          const next = prev.slice();
          next[idx] = { ...prev[idx], imageUrl: url };
          return next;
        });
      });
    }

    setMessages((prev) => {
      const merged = prev.concat(newMessages);
      // De-dupe in the unlikely event two snapshots emit the same id.
      const seen = new Set<string>();
      const out: ArrivalMessage[] = [];
      for (let i = merged.length - 1; i >= 0; i--) {
        const m = merged[i];
        if (seen.has(m.id)) continue;
        seen.add(m.id);
        out.unshift(m);
      }
      if (out.length > MAX_MESSAGES) {
        return out.slice(out.length - MAX_MESSAGES);
      }
      return out;
    });
  }, [vehicles, projections]);

  return { messages, error, ready: projections != null };
}
